import React, { PropsWithChildren, useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare } from '@fortawesome/free-solid-svg-icons';
import { faSquare } from '@fortawesome/free-regular-svg-icons';
import 'bulma/css/bulma.min.css';

import { CellFunctions, CellActivationStatus, CellValidationStatus, CellValidator, CellNavigationDirection } from './editableTableCell/cellDto.ts';
import { EditableTableCell } from './editableTableCell/cell.tsx';
import { ParameterChoice } from '../scanTypes.ts';
import { truthyJoin } from '../utils.tsx';
import './editableTable.css';
import './sortableHeaders.css';


type Coordinates = {x: number, y: number};


// Signature of a function called with the current selection rect when a copy event occurs.
type EditableTableClipboardHandler = (event: React.ClipboardEvent, rectLower: Coordinates, rectHigher: Coordinates) => void;

interface EditableTableCellSettings {
  value: any;
  // Used if defined, otherwise value is used for display (and assumed to be a string).
  displayValue?: string;
  key: string;
  description?: string;
  // If this is set, a read-only alert will be appended to the description,
  // and all edits to the cell will revert to the previous value.
  isReadOnly?: boolean;
  isUnused?: boolean;
  isInvalid?: boolean;
  // This parameter is used to indicate that a CellAutocomplete should be
  // used instead of a CellTextfield.
  choices?: ParameterChoice[];
  // If set, the cell will present as a checkbox, and the value will be interpreted as a boolean.
  isBoolean?: boolean;
  useModal?: boolean;
  modalTitle?: string;
  customModalContent?: (value: any, onValueChange: (input: any) => void) => React.JSX.Element;
}


// These are the functions needed by the editable table.
interface EditableTableFunctions {
  renderHeaderCell?: (x: number) => React.JSX.Element;
  getTableCellSettings?: (coordinates: Coordinates) => EditableTableCellSettings;
  isCellValidForEditing?: (coordinates: Coordinates) => boolean;
  cellValidate: CellValidator;
  cellSave: CellValidator;
  onCopy: EditableTableClipboardHandler;
  onPaste: EditableTableClipboardHandler;
  rowSelectionChanged?: (selectedRows: number[]) => void;
}


interface EditableTableProps {
  rowIdentifiers: string[];
  columnCount: number;
  hiddenColumns?: number[];
  displayRowSelectionBoxes: boolean;
  tableFunctions: EditableTableFunctions;
}


enum ClickSearchStatus { FoundCoordinates, NotFound, Ignore };

// Given a DOM node, walks up the tree looking for a node of type "td"
// with "sampleX" and "sampleY" values in its dataset.
// If those values are present, return them, otherwise return null.
function findCoordinatesForClick(node: HTMLElement): [ClickSearchStatus, Coordinates | undefined] {
  var n = node.nodeName || "";
  n = n.trim().toLowerCase();
  if (node.dataset) {
    const ic = node.dataset.ignoreClick;
    if (ic === "true") {
      return [ClickSearchStatus.Ignore, undefined];
    }
    if (n == "td") {
      const x = node.dataset.sampleX;
      const y = node.dataset.sampleY;
      if ((x !== undefined) && (y !== undefined)) {
        return [ClickSearchStatus.FoundCoordinates, { x: parseInt(x, 10), y: parseInt(y, 10)}];
      }
    }
  }
  const p = node.parentNode;
  if (!p) { return [ClickSearchStatus.NotFound, undefined]; } else { return findCoordinatesForClick(p as HTMLElement); }
}


// Given a DOM node, walks up the tree looking for a node of type "table".
// If found, return it, otherwise return null.
function findEnclosingTable(node: HTMLElement): HTMLElement | null {
  var n = node.nodeName || "";
  if (n.trim().toLowerCase() == "table") { return node; }
  const p = node.parentNode;
  if (!p) { return null; } else { return findEnclosingTable(p as HTMLElement); }
}


const EditableTable: React.FC<PropsWithChildren<EditableTableProps>> = (props) => {

  const [tableHasFocus, setTableHasFocus] = useState<boolean>(false);
  const [cellFocusX, setCellFocusX] = useState<number | null>(null);
  const [cellFocusY, setCellFocusY] = useState<number | null>(null);
  const [lastActivationMethod, setLastActivationMethod] = useState<CellActivationStatus>(CellActivationStatus.Inactive);
  const tableRef = useRef<HTMLTableElement>(null);

  const [cellMouseDown, setCellMouseDown] = useState<boolean>(false);
  const [cellMouseDownX, setCellMouseDownX] = useState<number | null>(null);
  const [cellMouseDownY, setCellMouseDownY] = useState<number | null>(null);
  const [cellMouseMoveX, setCellMouseMoveX] = useState<number | null>(null);
  const [cellMouseMoveY, setCellMouseMoveY] = useState<number | null>(null);

  const hiddenColumnSet = new Set(props.hiddenColumns ?? []);


  // Triggered one time only, whenever the input is blurred.
  // Used to ensure the latest state values are being validated (instead of old ones),
  // after a click event on a pulldown item.
  useEffect(() => {
    if (!tableHasFocus) {
    }
  }, [tableHasFocus]);


  // When a mouse move changes the selection area, report a change in selected rows.
  useEffect(() => {
    if (props.displayRowSelectionBoxes && props.tableFunctions.rowSelectionChanged) {
      const selectedRows:number[] = [];
      if ((cellMouseDownY !== null) && (cellMouseMoveY !== null)) {
        const lowerY = Math.min(cellMouseDownY ?? 0, cellMouseMoveY ?? 0);
        const higherY = Math.max(cellMouseDownY ?? 0, cellMouseMoveY ?? 0);
        for (var i = lowerY; i <= higherY; i++) {
          selectedRows.push(i);
        }
      }
      props.tableFunctions.rowSelectionChanged(selectedRows);
    }
  }, [cellMouseDownY, cellMouseMoveY, props.rowIdentifiers]);


  function tableOnFocus(event: React.FocusEvent<HTMLElement>) {
    if ((event.target.nodeName.toLowerCase() == "table") && event.target.classList.contains("editabletable")) {
      setTableHasFocus(true);
    }
  }

  function tableOnBlur(event: React.FocusEvent<HTMLElement>) {
    if ((event.target.nodeName.toLowerCase() == "table") && event.target.classList.contains("editabletable")) {
      setTableHasFocus(false);
    }
  }


  function tableOnPointerDown(event: React.PointerEvent<HTMLElement>) {
    const [status, foundCell] = findCoordinatesForClick(event.target as HTMLElement);
    if (status === ClickSearchStatus.FoundCoordinates && foundCell) {
      setCellMouseDown(true);
      setCellMouseDownX(foundCell.x);
      setCellMouseDownY(foundCell.y);
      setCellMouseMoveX(foundCell.x);
      setCellMouseMoveY(foundCell.y);

      // We can't do this because it creates a reference to the function in a previous
      // instance of the SampleTable react object, which contains the old useState values,
      // sowing chaos and confusion.
      // document.addEventListener("mouseup", tableOnMouseUp, {once: true});

      // We can't do this either because it redirects all subsequent events so their
      // target is the table itself, obscuring the element that the pointer was originally over,
      // making the event useless to us.
      //const foundTable = findEnclosingTable(event.target as HTMLElement);
      //if (foundTable) {
      //  foundTable.setPointerCapture(event.pointerId);
      //}
    } else if (status === ClickSearchStatus.NotFound) {
      setCellMouseDown(false);
      setCellMouseDownX(null);
      setCellMouseDownY(null);
      setCellMouseMoveX(null);
      setCellMouseMoveY(null);
    }
  }


  function tableOnPointerOver(event: React.PointerEvent<HTMLElement>) {
    // We do no tracking if the mouse isn't down
    if (!cellMouseDown) { return; }
    const [status, foundCell] = findCoordinatesForClick(event.target as HTMLElement);
    if (status === ClickSearchStatus.FoundCoordinates && foundCell) {
      if ((foundCell.x != cellMouseMoveX) || (foundCell.y != cellMouseMoveY)) {
        setCellMouseMoveX(foundCell.x);
        setCellMouseMoveY(foundCell.y);
      }
    }
  }


  function tableOnDoubleClick(event: React.MouseEvent<HTMLElement>) {
    const [status, foundCell] = findCoordinatesForClick(event.target as HTMLElement);
    if (status === ClickSearchStatus.FoundCoordinates && foundCell) {
      setCellMouseDownX(foundCell.x);
      setCellMouseDownY(foundCell.y);
      setCellMouseMoveX(foundCell.x);
      setCellMouseMoveY(foundCell.y);
      setCellFocusX(foundCell.x);
      setCellFocusY(foundCell.y);
      setCellMouseDown(false);
      setLastActivationMethod(CellActivationStatus.ByMouse);
    }
  }


  function tableOnPointerUp(event: React.PointerEvent<HTMLElement>) {
    const [status, foundCell] = findCoordinatesForClick(event.target as HTMLElement);
    if (status === ClickSearchStatus.FoundCoordinates && foundCell) {
      // Mouse up on a cell currently being edited?  Ignore it so the cell can handle it.
      if ((cellFocusX != foundCell.x) || (cellFocusY != foundCell.y)) {
        setCellMouseDown(false);
        setCellFocusX(null);
        setCellFocusY(null);
      }
    } else if (status === ClickSearchStatus.NotFound) {
      setCellMouseDown(false);
      setCellFocusX(null);
      setCellFocusY(null);
    }
  }


  function tableOnKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    if (event.key == "Enter") {
      // If we're already in edit mode for a cell, ignore this.
      if ((cellFocusX !== null) || (cellFocusY !== null)) {
        return;
      }
      // If we don't have coordinates for a recent mouse down event, ignore this.
      if ((cellMouseDownX === null) || (cellMouseDownY === null)) {
        return;
      }
      event.preventDefault();
      setCellFocusX(cellMouseDownX);
      setCellFocusY(cellMouseDownY);
      setLastActivationMethod(CellActivationStatus.ByKeyboard);
    } else if (event.key == "Escape") {
      if ((cellFocusX === null) || (cellFocusY === null)) {
        return;
      }

      // If we're intercepting an Escape key from a cell that is currently being edited,
      // we should attempt to focus the table.
      // We don't want to refocus on "close" coming from a cell, because that is called
      // (among other times) when the cell experiences a blur event,
      // which can happen because the user is clicking outside the entire table.
      if (tableRef.current) {
        tableRef.current.focus();
      }
    }

    // We'll only check for movement if we're not currently editing AND there's already a selection.
    if ((cellFocusX == null) && (cellFocusY == null)) {
      if ((cellMouseDownX !== null) && (cellMouseDownY !== null)) {
        var travelVector: Coordinates | null = null;
        switch (event.key) {
          case "ArrowUp":
            travelVector = {x: 0, y: -1};
            break;
          case "ArrowDown":
            travelVector = {x: 0, y: 1};
            break;
          case "ArrowLeft":
            travelVector = {x: -1, y: 0};
            break;
          case "ArrowRight":
            travelVector = {x: 1, y: 0};
            break;
        }
        if (travelVector !== null) {
          event.preventDefault();
          if (event.shiftKey && (cellMouseMoveX !== null) && (cellMouseMoveY !== null)) {
            const found = lookForAdjacentCell({x: cellMouseMoveX, y: cellMouseMoveY}, travelVector, true);
            if (found !== null) {
              setCellMouseMoveX(found.x);
              setCellMouseMoveY(found.y);
            }
          } else {
            var found:Coordinates | null = null;
            if ((cellMouseMoveX !== null) && (cellMouseMoveY !== null)) {
              found = lookForAdjacentCell({x: cellMouseMoveX, y: cellMouseMoveY}, travelVector, true);
            } else {
              found = lookForAdjacentCell({x: cellMouseDownX, y: cellMouseDownY}, travelVector, true);
            }
            if (found !== null) {
              setCellMouseDownX(found.x);
              setCellMouseDownY(found.y);
              setCellMouseMoveX(found.x);
              setCellMouseMoveY(found.y);
            }
          }
        }
      }
    }
  }


  function isWithinSelection(x: number, y: number) {
    if ((cellMouseDownX !== null) && (cellMouseDownY !== null) &&
        (cellMouseMoveX !== null) && (cellMouseMoveY !== null)) {
      if ((x <= Math.max(cellMouseDownX, cellMouseMoveX)) && 
          (x >= Math.min(cellMouseDownX, cellMouseMoveX))) { 
        if ((y <= Math.max(cellMouseDownY, cellMouseMoveY)) && 
            (y >= Math.min(cellMouseDownY, cellMouseMoveY))) { 
          return true;
        }
      }
    }
    return false;
  }


  function isWithinSelectionRow(y: number) {
    if ((cellMouseDownY !== null) && (cellMouseMoveY !== null)) {
      if ((y <= Math.max(cellMouseDownY, cellMouseMoveY)) && 
          (y >= Math.min(cellMouseDownY, cellMouseMoveY))) { 
        return true;
      }
    }
    return false;
  }


  // To properly draw a selection border around certain cells in a table,
  // one must apply styles to cells on both sides of the line defining the border.
  // A mismatch in styles will have unpredictable effects and usually fail to render.
  function selectionBorderClasses(x: number, y: number) {
    // If the table doesn't have focus, don't show the selection
    // to avoid confusion over what else might be selected for copy/paste on the page.
    if (!tableHasFocus) { return []; }
    // If there is a cell in focus for editing, we don't draw the selection border.
    if ((cellFocusX !== null) && (cellFocusY !== null)) {
      return [];
    }
    var borderClasses = [];
    // It's important to skip over hidden columns so we apply styles only on visible cells.
    const up = lookForAdjacentCell({x: x, y: y}, {x: 0, y: -1}, false);
    const down = lookForAdjacentCell({x: x, y: y}, {x: 0, y: 1}, false);
    const left = lookForAdjacentCell({x: x, y: y}, {x: -1, y: 0}, false);
    const right = lookForAdjacentCell({x: x, y: y}, {x: 1, y: 0}, false);

    const inSelection = isWithinSelection(x, y);
    const upInSelection = up ? isWithinSelection(up.x, up.y) : false;
    const downInSelection = down ? isWithinSelection(down.x, down.y) : false;
    const leftInSelection = left ? isWithinSelection(left.x, left.y) : false;
    const rightInSelection = right ? isWithinSelection(right.x, right.y) : false;

    // If one is true and the other false, regardless of which one, we're on either side of a transition
    // between "selected" and "not selected", and should set a border style.
    if (inSelection != upInSelection) {
      borderClasses.push("selectionTop");
    }
    if (inSelection != downInSelection) {
      borderClasses.push("selectionBottom");
    }
    if (inSelection != leftInSelection) {
      borderClasses.push("selectionLeft");
    }
    if (inSelection != rightInSelection) {
      borderClasses.push("selectionRight");
    }
    if (inSelection) {
      borderClasses.push("inSelection");
    }
    return borderClasses;
  }


  function tableOnCopy(event: React.ClipboardEvent) {
    // If it's an element within the table, don't intercept the event.
    // We're only interested in copy events where the table element itself has focus.
    if (!tableHasFocus) { return; }
    // A cell is being edited directly; ignore this event.
    if ((cellFocusY !== null) || (cellFocusX !== null)) { return; }
    // No rows selected
    if ((cellMouseDownY === null) || (cellMouseMoveY === null)) { return; }
    // No columns selected
    if ((cellMouseDownX === null) || (cellMouseMoveX === null)) { return; }

    // The rect of the current selection.
    const lowerX = Math.min(cellMouseDownX, cellMouseMoveX);
    const lowerY = Math.min(cellMouseDownY, cellMouseMoveY);
    const higherX = Math.max(cellMouseDownX, cellMouseMoveX);
    const higherY = Math.max(cellMouseDownY, cellMouseMoveY);

    if (props.tableFunctions.onCopy) {
      props.tableFunctions.onCopy(event, { x: lowerX, y: lowerY }, { x: higherX, y: higherY });
    }
  }


  function tableOnPaste(event: React.ClipboardEvent) {
    // If it's an element _within_ the table, don't intercept the event.
    // We're only interested in copy events where the table element itself has focus.
    if (!tableHasFocus) { return; }
    // If a cell is being edited directly, don't intercept.
    if ((cellFocusY !== null) || (cellFocusX !== null)) { return; }
    // No rows selected?  Ignore.
    if ((cellMouseDownY === null) || (cellMouseMoveY === null)) { return; }
    // No columns selected?  Ignore.
    if ((cellMouseDownX === null) || (cellMouseMoveX === null)) { return; }

    // The rect of the current selection. May be re-formed in the process of pasting.
    const lowerX = Math.min(cellMouseDownX, cellMouseMoveX);
    const lowerY = Math.min(cellMouseDownY, cellMouseMoveY);
    const higherX = Math.max(cellMouseDownX, cellMouseMoveX);
    const higherY = Math.max(cellMouseDownY, cellMouseMoveY);

    if (props.tableFunctions.onPaste) {
      props.tableFunctions.onPaste(event, { x: lowerX, y: lowerY }, { x: higherX, y: higherY });
    }
  }

  
  // Add the vector to the given currentPosition until it points to a table cell that is
  // valid for editing, then return that position, or return null if we run off the edge of the table.
  function lookForAdjacentCell(currentPosition: Coordinates, vector: Coordinates, requireEditable: boolean): Coordinates | null {
    // Sanity check
    if ((vector.x == 0) && (vector.y == 0)) { return null; }
    const nextPosition = { x: currentPosition.x + vector.x, y: currentPosition.y + vector.y };
    const xMax = props.columnCount;
    const yMax = props.rowIdentifiers.length;
    if ((nextPosition.x < 0) || (nextPosition.y < 0) || (nextPosition.x >= xMax) || (nextPosition.y >= yMax)) {
      return null;
    }

    var valid = true;
    if (requireEditable && props.tableFunctions.isCellValidForEditing) {
      valid = props.tableFunctions.isCellValidForEditing(nextPosition);
    }
    // Hidden column? Keep looking.
    if (hiddenColumnSet.has(nextPosition.x)) {
      valid = false;
    }

    if (valid) {
      return nextPosition;
    } else {
      return lookForAdjacentCell(nextPosition, vector, requireEditable);
    }
  }


  // Use lookForAdjacentCell to walk along the table from the given cell coordinates,
  // in the given directon, and switch editing to the first editable cell we find.
  function switchEditingToNearbyCell(currentPosition: Coordinates, vector: Coordinates): CellValidationStatus {
    const found = lookForAdjacentCell(currentPosition, vector, true);
    if (found === null) {
      return CellValidationStatus.Failure;
    }
    setCellFocusX(found.x);
    setCellFocusY(found.y);
    return CellValidationStatus.Success;
  }


  function moveBetweenCells(x: number, y: number, d: CellNavigationDirection): CellValidationStatus {
    var travelVector: Coordinates;
    switch (d) {
      // Seek upward in the table for a nearby editable cell and switch to it if available.
      case CellNavigationDirection.Up:
        travelVector = {x: 0, y: -1};
        break;
      // Seek downward in the table for a nearby editable cell and switch to it if available.
      case CellNavigationDirection.Down:
        travelVector = {x: 0, y: 1};
        break;
      // Seek left in the table for a nearby editable cell and switch to it if available.
      case CellNavigationDirection.Left:
        travelVector = {x: -1, y: 0};
        break;
      // Seek right in the table for a nearby editable cell and switch to it if available.
      case CellNavigationDirection.Right:
        travelVector = {x: 1, y: 0};
        break;
    }
    const result = switchEditingToNearbyCell({x: x, y: y}, travelVector);
    if (result == CellValidationStatus.Success) {
        setLastActivationMethod(CellActivationStatus.ByKeyboard);
    }
    return result;
  }


  // A callback function passed to every EditableTableCell (non-header table cell in samples table)
  // that should be called if the cell is in editing mode and wishes to close.
  function cellClose(x: number, y: number): void {
    // We only act on a "close" call coming from the cell that's currently
    // selected for editing.  If some other cell is selected, the caller is effectively already closed.
    if ((cellFocusX == x) && (cellFocusY == y)) {
      setCellFocusX(null);
      setCellFocusY(null);
      setCellMouseDownX(cellFocusX);
      setCellMouseDownY(cellFocusY);
      setCellMouseMoveX(cellFocusX);
      setCellMouseMoveY(cellFocusY);
    }
  }

  var headers: React.JSX.Element[] = [];
  if (props.tableFunctions.renderHeaderCell) {
    if (props.displayRowSelectionBoxes && props.tableFunctions.rowSelectionChanged) {
      headers.push(
        <th key="_selection" className="rowselectionheader"></th>
      );
    }
    for (var x = 0; x < props.columnCount; x++) {
      if (!hiddenColumnSet.has(x)) {
        headers.push(props.tableFunctions.renderHeaderCell(x));
      }
    }
  }

  var rows: React.JSX.Element[] = [];
  const getTableCellSettings = props.tableFunctions.getTableCellSettings;
  if (typeof getTableCellSettings === "function") {

    // All the functions we need to pass to each editable cell in the table.
    const cellFunctions: CellFunctions = {
      validate: props.tableFunctions.cellValidate,
      save: props.tableFunctions.cellSave,
      close: cellClose,
      move: moveBetweenCells
    };

    props.rowIdentifiers.forEach((rowId, y) => {
      var cells: React.JSX.Element[] = [];
      if (props.displayRowSelectionBoxes && props.tableFunctions.rowSelectionChanged) {
        cells.push(
          <td key={`selection-${y}`} className={truthyJoin("rowselectioncell", isWithinSelectionRow(y) && "selected")}>
            <span className="icon">
              <FontAwesomeIcon icon={isWithinSelectionRow(y) ? faCheckSquare : faSquare} />
            </span>
          </td>
        );
      }
      for (var x = 0; x < props.columnCount; x++) {
        if (hiddenColumnSet.has(x)) {
          continue;
        }

        const activationStatus = ((cellFocusX === x) && (cellFocusY === y)) ? lastActivationMethod : CellActivationStatus.Inactive;
        const withinSelection = isWithinSelection(x, y);
        const cellSettings = getTableCellSettings({x: x, y: y});
        const cellClasses = truthyJoin(
                "editablecell",
                (cellSettings.isInvalid && "invalid"),
                (cellSettings.isUnused && "unused"),
                (cellSettings.isReadOnly && "readonly"),
                ...selectionBorderClasses(x, y)
              );
        cells.push((
          <td key={ `cell-${x}-${y}` }
            data-sample-x={ x }
            data-sample-y={ y }
            data-sample-readonly={ cellSettings.isReadOnly || 0 }
            data-sample-unused={ cellSettings.isUnused || 0 }
            className={ cellClasses }
          >
            <EditableTableCell x={x} y={y}
              key={ cellSettings.key }
              activationStatus={ activationStatus }
              value={ cellSettings.value }
              displayValue={ cellSettings.displayValue }
              description={ cellSettings.description }
              isReadOnly={ cellSettings.isReadOnly }
              isUnused={ cellSettings.isUnused }
              isWithinSelection={ withinSelection }
              choices={ cellSettings.choices }
              isBoolean={ cellSettings.isBoolean }
              useModal={ cellSettings.useModal }
              modalTitle={ cellSettings.modalTitle }
              customModalContent={ cellSettings.customModalContent }
              cellFunctions={ cellFunctions }
            />
          </td>
        ));
      }
      rows.push( <tr key={ `row-${rowId}` }>{ cells }</tr> );
    });
  }

  return (
    <table className="editabletable sortableheaders striped cellhover"
            tabIndex={0}
            ref={ tableRef }
            onFocus={ tableOnFocus }
            onBlur={ tableOnBlur }
            onCopy={ tableOnCopy }
            onPaste={ tableOnPaste }
            onDoubleClick={ tableOnDoubleClick }
            onPointerOver={ tableOnPointerOver }
            onPointerDown={ tableOnPointerDown }
            onPointerUp={ tableOnPointerUp }
            onKeyDown={ tableOnKeyDown }
        >
      <thead>
        <tr key="headers">
          { headers }
        </tr>
      </thead>
      <tbody>
        { rows }
      </tbody>
    </table>
  );
}

export { EditableTable };
export type { Coordinates, EditableTableCellSettings, EditableTableFunctions };